<script setup lang="ts">
/**
 * Building a meal's menu (SPEC §6.14, §6.17, §6.19, §8.14).
 *
 * The same editor the templates use, with the one thing an event has that a
 * template does not: what families have already promised. That sits beside each
 * item, because it is the fact that changes what an organizer is willing to do
 * to the menu.
 *
 * Which is also why saving here can be refused. An edit that would take away a
 * commitment comes back naming the households, and the organizer decides
 * (SPEC §6.19, MT-090).
 */
import { computed, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
import ChangeNoticePanel from '@/components/ChangeNoticePanel.vue'
import MenuEditor from '@/components/MenuEditor.vue'
import type { MenuItemDraft, MenuSectionDraft } from '@/components/menuEditor'
import {
  ApiError,
  endpoints,
  type CancellationNoticeResponse,
  type EventMenuResponse,
  type MealEventResponse,
  type MealTemplateSummaryResponse,
} from '@/api'
import { asNumber } from '@/utils/numbers'

/** The losses a refused save comes back with. Only ever a problem extension. */
interface MenuChangeReview {
  losses: Array<{
    eventMenuItemId: string
    itemName: string
    reason: string
    households: string[]
    quantity: number
  }>
  households: string[]
}

const route = useRoute()
const router = useRouter()

const groupId = computed(() => String(route.params.groupId))
const eventId = computed(() => String(route.params.eventId))

const meal = ref<MealEventResponse | null>(null)
const menu = ref<EventMenuResponse | null>(null)
const sections = ref<MenuSectionDraft[]>([])
const templates = ref<MealTemplateSummaryResponse[]>([])

const loading = ref(true)
const saving = ref(false)
const error = ref<string | null>(null)
const message = ref<string | null>(null)

/** What a refused save says would be lost, and what to do about it. */
const review = ref<MenuChangeReview | null>(null)
const pending = ref<'save' | { template: string } | null>(null)

const applying = ref(false)
const chosenTemplate = ref<string | null>(null)

const savingAsTemplate = ref(false)
const templateName = ref('')

const confirmingCancel = ref(false)

/**
 * What to send, once the meal is off (SPEC §6.24.1).
 *
 * Only ever set after the cancellation has actually happened, so the message
 * describes a fact rather than an intention. A draft comes back saying no
 * notice is needed and this stays null.
 */
const notice = ref<CancellationNoticeResponse | null>(null)
const copied = ref(false)

/** Whether the organizer chose not to email the cancellation (MT-163). */
const skippedEmail = ref(false)

const saved = ref('')
const dirty = computed(() => JSON.stringify(sections.value) !== saved.value)
const leaving = ref<(() => void) | null>(null)

/** Whether the menu can still be changed at all (SPEC §6.14). */
const editable = computed(() => menu.value?.isEditable ?? false)

function describe(caught: unknown): string {
  return (caught instanceof ApiError && caught.problem?.title) || 'That did not work.'
}

/**
 * The review a 409 carries, when it carries one.
 *
 * Anything else with that status is a different conflict and belongs in the
 * ordinary error line rather than in a confirmation nobody asked for.
 */
function reviewFrom(caught: unknown): MenuChangeReview | null {
  if (!(caught instanceof ApiError) || caught.status !== 409) return null

  const problem = caught.problem as { review?: MenuChangeReview } | undefined

  return problem?.review ?? null
}

function adopt(next: EventMenuResponse) {
  menu.value = next
  sections.value = next.sections.map((section) => ({
    id: section.id,
    name: section.name,
    items: section.items.map((item) => ({
      id: item.id,
      name: item.name,
      notes: item.notes ?? null,
      participationMode: item.participationMode as MenuItemDraft['participationMode'],
      quantityNeeded: asNumber(item.quantityNeeded),
      unitLabel: item.unitLabel ?? null,
      isFlexible: item.isFlexible,
      allowOverClaim: item.allowOverClaim,
    })),
  }))

  saved.value = JSON.stringify(sections.value)
}

/** What has been promised against an item, for the line under it. */
function promised(itemId: string | null): { claimed: number; remaining: number | null } | null {
  if (!itemId || !menu.value) return null

  for (const section of menu.value.sections) {
    const found = section.items.find((i) => i.id === itemId)

    if (found && found.participationMode === 'SignupRequired') {
      return {
        claimed: asNumber(found.claimedQuantity) ?? 0,
        remaining: asNumber(found.remainingQuantity),
      }
    }
  }

  return null
}

async function load() {
  loading.value = true
  error.value = null

  try {
    const [event, current, list] = await Promise.all([
      endpoints.getMealEvent(groupId.value, eventId.value),
      endpoints.getEventMenu(groupId.value, eventId.value),
      endpoints.listMealTemplates(groupId.value),
    ])

    meal.value = event
    templates.value = list
    adopt(current)
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    loading.value = false
  }
}

async function save(confirm = false) {
  saving.value = true
  error.value = null
  message.value = null

  try {
    adopt(
      await endpoints.saveEventMenu(groupId.value, eventId.value, {
        confirm,
        sections: sections.value.map((section) => ({
          id: section.id,
          name: section.name,
          items: section.items.map((item) => ({
            id: item.id,
            name: item.name,
            notes: item.notes,
            participationMode: item.participationMode,
            quantityNeeded: item.quantityNeeded,
            unitLabel: item.unitLabel,
            isFlexible: item.isFlexible,
            allowOverClaim: item.allowOverClaim,
          })),
        })),
      }),
    )

    review.value = null
    pending.value = null
    message.value = 'Saved.'
  } catch (caught) {
    const needed = reviewFrom(caught)

    if (needed) {
      review.value = needed
      pending.value = 'save'
    } else {
      error.value = describe(caught)
    }
  } finally {
    saving.value = false
  }
}

async function applyTemplate(templateId: string, confirm = false) {
  saving.value = true
  error.value = null
  message.value = null

  try {
    await endpoints.assignMealTemplateToEvent(groupId.value, eventId.value, {
      mealTemplateId: templateId,
      confirm,
    })

    adopt(await endpoints.getEventMenu(groupId.value, eventId.value))

    review.value = null
    pending.value = null
    applying.value = false
    message.value = 'Menu replaced.'
  } catch (caught) {
    const needed = reviewFrom(caught)

    if (needed) {
      review.value = needed
      pending.value = { template: templateId }
    } else {
      error.value = describe(caught)
    }
  } finally {
    saving.value = false
  }
}

/** Goes ahead with whatever the review was about. */
function confirmChange() {
  const what = pending.value

  review.value = null
  pending.value = null

  if (what === 'save') {
    void save(true)
  } else if (what) {
    void applyTemplate(what.template, true)
  }
}

async function saveAsTemplate() {
  savingAsTemplate.value = false
  saving.value = true
  error.value = null

  try {
    const template = await endpoints.saveEventAsTemplate(groupId.value, eventId.value, {
      name: templateName.value,
    })

    message.value = `Saved as ${template.name}. It is yours to reuse now.`
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    saving.value = false
  }
}

/**
 * Calls the meal off, then offers what to tell everybody.
 *
 * The prompt always appears for a meal anybody was told about, and the
 * primary action is to send: a family that shows up to a cancelled meal is
 * the worst outcome this product can produce.
 */
async function cancelMeal() {
  confirmingCancel.value = false
  saving.value = true
  error.value = null
  message.value = null

  try {
    meal.value = await endpoints.cancelMealEvent(groupId.value, eventId.value)
    adopt(await endpoints.getEventMenu(groupId.value, eventId.value))

    const result = await endpoints.cancellationNotice(groupId.value, eventId.value)

    if (result.needsNotice) {
      notice.value = result
      copied.value = false
      skippedEmail.value = false
    } else {
      // Nobody was told, so there is nothing to withdraw.
      notice.value = null
      message.value = 'Cancelled. Nobody had been told about it.'
    }
  } catch (caught) {
    error.value = describe(caught)
  } finally {
    saving.value = false
  }
}

async function copyNotice() {
  if (!notice.value) return

  try {
    await navigator.clipboard.writeText(notice.value.message)
    copied.value = true
  } catch {
    // A browser that refuses clipboard access is not an error worth showing:
    // the text is on screen and selectable.
    copied.value = false
  }
}

function openSaveAsTemplate() {
  templateName.value = meal.value?.title ?? ''
  savingAsTemplate.value = true
}

function back() {
  void router.push({
    name: 'organizer-event',
    params: { groupId: groupId.value, eventId: eventId.value },
  })
}

onBeforeRouteLeave((_to, _from, next) => {
  if (!dirty.value) {
    next()
    return
  }

  leaving.value = () => next()
})

function discard() {
  const go = leaving.value

  leaving.value = null
  saved.value = JSON.stringify(sections.value)
  go?.()
}

void load()
</script>

<template>
  <div class="meal">
    <header class="meal__head">
      <v-btn variant="text" density="comfortable" prepend-icon="mdi-arrow-left" @click="back">
        Meal
      </v-btn>

      <div class="meal__head-actions">
        <v-btn
          :disabled="loading || saving"
          variant="text"
          density="comfortable"
          @click="openSaveAsTemplate"
        >
          Save as template
        </v-btn>
        <v-btn
          :disabled="loading || saving || !editable"
          variant="text"
          density="comfortable"
          @click="applying = true"
        >
          Use a template
        </v-btn>
        <v-btn
          v-if="meal && meal.availableTransitions.includes('Cancelled')"
          :disabled="loading || saving"
          variant="text"
          density="comfortable"
          color="error"
          @click="confirmingCancel = true"
        >
          Cancel meal
        </v-btn>
        <v-btn
          :loading="saving"
          :disabled="loading || !editable"
          color="primary"
          variant="flat"
          @click="save(false)"
        >
          Save menu
        </v-btn>
      </div>
    </header>

    <div v-if="meal" class="meal__summary">
      <h1 class="meal__title">{{ meal.title }}</h1>
      <p class="meal__when">
        {{ meal.localDate }} at {{ meal.localTime }}
        <v-chip size="x-small" variant="tonal" class="ml-2">{{ meal.status }}</v-chip>
      </p>
    </div>

    <v-alert v-if="error" type="error" variant="tonal" class="mb-4">{{ error }}</v-alert>
    <v-alert v-if="message" type="success" variant="tonal" class="mb-4">{{ message }}</v-alert>

    <v-alert v-if="!loading && !editable" type="info" variant="tonal" class="mb-4">
      This meal is {{ meal?.status?.toLowerCase() }}, so its menu is a record of what happened
      rather than something to change.
    </v-alert>

    <v-skeleton-loader v-if="loading" type="article" />

    <MenuEditor v-else v-model="sections" :disabled="saving || !editable">
      <template #item-meta="{ item }">
        <p v-if="promised(item.id)" class="meal__promised">
          <template v-if="promised(item.id)!.remaining === null">
            {{ promised(item.id)!.claimed }} promised
          </template>
          <template v-else-if="promised(item.id)!.remaining! > 0">
            {{ promised(item.id)!.claimed }} promised, {{ promised(item.id)!.remaining }} still
            needed
          </template>
          <template v-else> {{ promised(item.id)!.claimed }} promised — covered </template>
        </p>
      </template>
    </MenuEditor>

    <v-dialog :model-value="applying" max-width="30rem" @update:model-value="applying = false">
      <v-card>
        <v-card-title tag="h2">Use a template</v-card-title>
        <v-card-text>
          <p class="meal__dialog-hint">
            This replaces the whole menu with a copy of the template. Editing the template later
            will not change this meal.
          </p>
          <v-select
            v-model="chosenTemplate"
            :items="templates"
            item-title="name"
            item-value="id"
            label="Template"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="applying = false">Cancel</v-btn>
          <v-btn
            :disabled="!chosenTemplate"
            variant="flat"
            color="primary"
            @click="applyTemplate(chosenTemplate!)"
          >
            Replace the menu
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog :model-value="review !== null" max-width="32rem" @update:model-value="review = null">
      <v-card v-if="review">
        <v-card-title tag="h2">Some families have already signed up</v-card-title>
        <v-card-text>
          <p class="meal__dialog-hint">
            Going ahead tells {{ review.households.join(', ') }} they no longer need to bring what
            they promised.
          </p>
          <ul class="meal__losses">
            <li v-for="loss in review.losses" :key="loss.eventMenuItemId">
              <strong>{{ loss.itemName }}</strong> — {{ loss.quantity }} promised by
              {{ loss.households.join(', ') }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="review = null">Leave the menu alone</v-btn>
          <v-btn variant="flat" color="primary" @click="confirmChange">Go ahead</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :model-value="savingAsTemplate"
      max-width="28rem"
      @update:model-value="savingAsTemplate = false"
    >
      <v-card>
        <v-card-title tag="h2">Save this menu as a template</v-card-title>
        <v-card-text>
          <p class="meal__dialog-hint">
            The menu, and nothing about what anybody promised for this meal.
          </p>
          <v-text-field
            v-model="templateName"
            label="Template name"
            variant="outlined"
            density="comfortable"
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="savingAsTemplate = false">Cancel</v-btn>
          <v-btn
            :disabled="templateName.trim() === ''"
            variant="flat"
            color="primary"
            @click="saveAsTemplate"
          >
            Save as template
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :model-value="confirmingCancel"
      max-width="30rem"
      @update:model-value="confirmingCancel = false"
    >
      <v-card>
        <v-card-title tag="h2">Call off {{ meal?.title }}?</v-card-title>
        <v-card-text>
          The meal stays on your families' screens marked cancelled, so nobody turns up to it. It is
          not deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="confirmingCancel = false">Keep it</v-btn>
          <v-btn variant="flat" color="error" @click="cancelMeal">Cancel the meal</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
      The prompt always appears for a meal anybody was told about, and sending
      is the primary action. A family that shows up to a cancelled meal is the
      worst outcome this product can produce (SPEC §6.24.1).
    -->
    <v-dialog :model-value="notice !== null" max-width="32rem" @update:model-value="notice = null">
      <v-card v-if="notice">
        <v-card-title tag="h2">Tell them it is off</v-card-title>
        <v-card-text>
          <p class="meal__dialog-hint">
            {{ notice.householdCount }}
            {{ notice.householdCount === 1 ? 'household needs' : 'households need' }} to know:
            {{ notice.households.join(', ') }}.
          </p>
          <!--
            Email first, to every invited household that has a confirmed
            address (MT-163); the text below is for everybody else, or for the
            family group chat.
          -->
          <ChangeNoticePanel
            v-if="!skippedEmail"
            class="mb-4"
            :group-id="groupId"
            :event-id="eventId"
            kind="Cancelled"
            @done="(outcome) => (skippedEmail = outcome === 'skipped')"
          />
          <p class="meal__notice">{{ notice.message }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="notice = null">I will tell them myself</v-btn>
          <v-btn variant="outlined" @click="copyNotice">
            {{ copied ? 'Copied' : 'Copy the message' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :model-value="leaving !== null"
      max-width="28rem"
      @update:model-value="leaving = null"
    >
      <v-card>
        <v-card-title tag="h2">Leave without saving?</v-card-title>
        <v-card-text>Your changes to this menu will be lost.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="leaving = null">Keep editing</v-btn>
          <v-btn variant="flat" color="primary" @click="discard">Leave</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.meal {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.meal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.meal__head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.meal__title {
  font-size: 1.5rem;
  margin: 0;
}

.meal__when {
  color: rgb(var(--v-theme-on-surface-variant));
  margin: 0.25rem 0 1rem;
}

.meal__promised {
  color: rgb(var(--v-theme-on-surface-variant));
  font-size: 0.875rem;
  margin: 0;
}

.meal__dialog-hint {
  margin: 0 0 0.75rem;
}

.meal__losses {
  margin: 0;
  padding-left: 1.25rem;
}

/*
  Selectable, and looking like something to be copied rather than read. A
  browser that refuses clipboard access still leaves this usable.
*/
.meal__notice {
  margin: 0;
  padding: 0.75rem;
  border-radius: 0.375rem;
  background: rgb(var(--v-theme-surface-variant));
  user-select: all;
}
</style>
